import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { AlertCircle, Plus, X, Upload, Shield, ShieldCheck, BadgeCheck, ShieldX, ShieldQuestion, AlertTriangle, Loader2 } from 'lucide-react';
import { COUNTRY_OF_ORIGIN_OPTIONS, LICENSE_TYPE_OPTIONS, LICENSE_TYPE_LABELS } from '../../constants/compliance';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../hooks/use-toast';

// iter-fssai Phase 3 — mirrors StoreWideLicensesCard's near-expiry window
// (backend NEAR_EXPIRY_WINDOW_DAYS in services/compliance/fssai_verification.py).
const NEAR_EXPIRY_DAYS = 10;
const isNearExpiry = (lic) => {
  if (!lic.is_verified || !lic.expiry_date) return false;
  const exp = new Date(lic.expiry_date);
  const daysLeft = Math.ceil((exp - new Date()) / 86400000);
  return daysLeft <= NEAR_EXPIRY_DAYS;
};

// iter-fssai — Verifico verification badge, shared visual language with
// StoreWideLicensesCard's badge (kept as a local copy — different import
// path / no shared components dir between vendor/ and forms/ components).
const VerificationBadge = ({ lic }) => {
  if (lic.is_verified) {
    const exp = lic.expiry_date ? new Date(lic.expiry_date) : null;
    const daysLeft = exp ? Math.ceil((exp - new Date()) / 86400000) : null;
    if (daysLeft !== null && daysLeft < 0) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" data-testid="pdt-license-badge-expired">
          <ShieldX className="h-3 w-3" /> Expired on {lic.expiry_date}
        </span>
      );
    }
    if (daysLeft !== null && daysLeft <= 30) {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5" data-testid="pdt-license-badge-expiring">
          <AlertTriangle className="h-3 w-3" /> Verified · Expires in {daysLeft}d
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5" data-testid="pdt-license-badge-verified">
        <BadgeCheck className="h-3 w-3" /> Verified{lic.expiry_date ? ` · Exp: ${lic.expiry_date}` : ''}
      </span>
    );
  }
  if (lic.verification_status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5" title={lic.last_verification_error || ''} data-testid="pdt-license-badge-failed">
        <ShieldX className="h-3 w-3" /> Verification Failed
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-50 border border-gray-200 rounded-full px-2 py-0.5" data-testid="pdt-license-badge-unverified">
      <ShieldQuestion className="h-3 w-3" /> Not Verified
    </span>
  );
};

// iter-347 — Legal & Compliance card for the schema-driven Create/Edit
// Product form (UniversalSinglePageForm). Country of Origin + Licenses/
// Certificates are shown for every product; Shelf-Life and Warranty groups
// only appear when the selected category/subcategory has those rules
// toggled on in the CMS (Categories.jsx).
const ComplianceCard = ({
  formData,
  handleChange,
  complianceConfig = {},
  requiredLicenseType,
  shelfLifeFloorDays,
  shelfLifeGuaranteeWording,
  defaultLicenses = {},
  errors = {},
  isPhysicalProduct = true,
  listingId = null,
  isAdmin = false,
}) => {
  const { apiCall } = useAuth();
  const { toast } = useToast();
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [verifyingIdx, setVerifyingIdx] = useState(null);
  const [renewalModalIdx, setRenewalModalIdx] = useState(null);
  const [renewalChecked, setRenewalChecked] = useState(false);
  // iter-fssai Phase 3 — matches backend MAX_VENDOR_VERIFICATION_ATTEMPTS
  // (services/compliance/fssai_verification.py). Was previously 5.
  const MAX_ATTEMPTS = 3;
  const licenses = formData.licenses_certifications || [];

  const closeRenewalModal = () => {
    setRenewalModalIdx(null);
    setRenewalChecked(false);
  };

  const updateLicense = (idx, patch) => {
    const next = licenses.map((l, i) => {
      if (i !== idx) return l;
      // iter-374 — editing an inherited row turns it into a listing-owned
      // override, so a future profile-license edit won't silently
      // overwrite what was just typed here.
      const { source, ...rest } = l;
      return { ...rest, ...patch };
    });
    handleChange('licenses_certifications', next);
  };

  // iter-349 — Prefill a row's number/photo from the vendor's last-used
  // value for that license type, if the row doesn't already have one
  const applySavedDefault = (row) => {
    const saved = defaultLicenses[row.type];
    if (saved && !row.number) {
      return { ...row, number: saved.number || '', image_url: saved.image_url || row.image_url };
    }
    return row;
  };

  const addLicenseRow = () => {
    const newRow = applySavedDefault({ type: requiredLicenseType || 'FSSAI', number: '' });
    handleChange('licenses_certifications', [...licenses, newRow]);
  };

  const removeLicenseRow = (idx) => {
    const row = licenses[idx];
    const next = licenses.filter((_, i) => i !== idx);
    // iter-374 — removing an entry inherited from the vendor's profile
    // must also mark that type "excluded" for THIS listing, otherwise
    // the next profile-license edit would silently re-add it.
    if (row?.source === 'vendor_default' && row?.type) {
      const excluded = formData.excluded_license_types || [];
      handleChange('excluded_license_types', excluded.includes(row.type) ? excluded : [...excluded, row.type]);
    }
    handleChange('licenses_certifications', next);
  };

  const uploadCertImage = async (idx, file) => {
    setUploadingIdx(idx);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      const token = sessionStorage.getItem('token');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/media/upload?optimize=false`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: uploadFormData,
      });
      if (response.ok) {
        const data = await response.json();
        updateLicense(idx, { image_url: data.cdn_url || data.file_url || data.url });
      }
    } finally {
      setUploadingIdx(null);
    }
  };

  // iter-fssai — Manual "Verify" trigger for a listing-owned FSSAI row.
  // Only available on an already-saved listing (needs a listingId) —
  // never auto-triggered on save. A row inherited from the vendor's
  // Store Profile (source==='vendor_default') is verified from there
  // instead, since that's the actual owner of the license entry.
  const handleVerify = async (idx, renewalConfirmed = false) => {
    if (!listingId) return;
    setVerifyingIdx(idx);
    try {
      const res = await apiCall(`/api/listings/${listingId}/licenses/${idx}/verify`, {
        method: 'POST',
        body: JSON.stringify({ renewal_confirmed: renewalConfirmed }),
      });
      if (res.license) updateLicense(idx, res.license);
      switch (res.outcome) {
        case 'verified':
          toast({ title: 'Verified', description: `FSSAI license verified — expires ${res.license?.expiry_date || 'N/A'}.` });
          break;
        case 'already_verified':
          toast({ title: 'Already Verified', description: res.message || 'This FSSAI license is already verified — no need to re-verify.' });
          break;
        case 'needs_confirmation':
          toast({ title: 'Confirm Renewal', description: res.message || 'Please confirm you have renewed this license, then re-verify.' });
          break;
        case 'blocked_attempts':
          toast({ title: 'Verification Limit Reached', description: res.message, variant: 'destructive' });
          break;
        case 'blocked_concurrent':
          toast({ title: 'Please Wait', description: res.message, variant: 'destructive' });
          break;
        case 'technical_failure':
          toast({ title: 'Verifico Unavailable', description: res.message || 'Verification service is temporarily unavailable — your attempt was not counted. Please try again shortly.', variant: 'destructive' });
          break;
        default:
          toast({ title: 'Verification Failed', description: res.message || res.license?.last_verification_error || 'Could not verify this license.', variant: 'destructive' });
      }
    } catch (error) {
      const msg = error.message || '';
      if (msg.toLowerCase().includes('out of range')) {
        toast({ title: 'Save Required', description: 'Please save this product first, then verify this license.', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: msg || 'Failed to verify license', variant: 'destructive' });
      }
    } finally {
      setVerifyingIdx(null);
    }
  };

  // Auto-add the category's required license row (locked type) so vendors
  // can't skip it — pre-filled with their last-used number for that type
  useEffect(() => {
    if (!requiredLicenseType) return;
    const already = licenses.some((l) => l.type === requiredLicenseType);
    if (!already) {
      const newRow = applySavedDefault({ type: requiredLicenseType, number: '' });
      handleChange('licenses_certifications', [...licenses, newRow]);
    }
  }, [requiredLicenseType, defaultLicenses]); // eslint-disable-line

  return (
    <Card data-testid="compliance-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Legal &amp; Compliance Information</CardTitle>
        <p className="text-xs text-gray-500">Required disclosures under Consumer Protection (E-Commerce) Rules — shown on the product page.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* Country of Origin */}
        <div className="space-y-2">
          <Label className={errors.country_of_origin ? 'text-red-600' : ''}>
            Country of Origin <span className="text-red-500">*</span>
          </Label>
          <Select value={formData.country_of_origin || ''} onValueChange={(v) => handleChange('country_of_origin', v)}>
            <SelectTrigger data-testid="field-country_of_origin" className={errors.country_of_origin ? 'border-red-500' : ''}>
              <SelectValue placeholder="Select country" />
            </SelectTrigger>
            <SelectContent>
              {COUNTRY_OF_ORIGIN_OPTIONS.map((c) => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {errors.country_of_origin && (
            <p className="text-xs text-red-600 flex items-center gap-1" data-testid="country-of-origin-error">
              <AlertCircle className="h-3 w-3" />{errors.country_of_origin}
            </p>
          )}
        </div>

        {/* iter-350 — Buying Mode (Cart vs Enquiry). Product-only — Service/
            Event/etc listings manage their own booking flow (Instant/
            Request/Enquiry template presets) elsewhere and don't use this
            Cart-vs-Enquiry toggle. */}
        {isPhysicalProduct && (
        <div className="space-y-2">
          <Label>Buying Mode</Label>
          {complianceConfig.buying_mode_override_enabled ? (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 flex items-start gap-2"
              data-testid="buying-mode-locked-banner"
            >
              <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
              <span>
                Locked to <strong>{complianceConfig.buying_mode_override_type === 'cart' ? 'Cart / Buy Now' : 'Enquiry Only (WhatsApp)'}</strong> by admin policy
                for this category (temporary compliance hold). Your own selection below is saved and will apply automatically once this is lifted.
              </span>
            </div>
          ) : null}
          <Select
            value={formData.bookingMode || 'instant'}
            disabled={complianceConfig.buying_mode_override_enabled}
            onValueChange={(v) => handleChange('bookingMode', v)}
          >
            <SelectTrigger data-testid="field-buying-mode" className={complianceConfig.buying_mode_override_enabled ? 'opacity-60' : ''}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">Cart / Buy Now</SelectItem>
              <SelectItem value="enquiry">Enquiry Only (WhatsApp)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-400">
            "Enquiry Only" hides Add to Cart / Buy Now on the product page and shows Enquire Now + Chat on WhatsApp instead.
          </p>
        </div>
        )}

        {/* Net Quantity (Legal Metrology declaration — physical products only) */}
        {isPhysicalProduct && formData.productType !== 'digital' && (
          <div className="space-y-2">
            <Label className={errors.net_quantity_value ? 'text-red-600' : ''}>
              Net Quantity <span className="text-red-500">*</span>
            </Label>
            <p className="text-xs text-gray-500">Package content declaration required under Legal Metrology Rules (separate from shipping weight).</p>
            <div className="flex gap-2">
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.net_quantity_value || ''}
                onChange={(e) => handleChange('net_quantity_value', e.target.value)}
                placeholder="e.g., 500"
                className={`flex-1 ${errors.net_quantity_value ? 'border-red-500' : ''}`}
                data-testid="field-net_quantity_value"
              />
              <Select
                value={formData.net_quantity_unit || 'g'}
                onValueChange={(v) => handleChange('net_quantity_unit', v)}
              >
                <SelectTrigger className="w-28" data-testid="field-net_quantity_unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="g">g</SelectItem>
                  <SelectItem value="kg">kg</SelectItem>
                  <SelectItem value="ml">ml</SelectItem>
                  <SelectItem value="L">L</SelectItem>
                  <SelectItem value="pieces">pieces</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {errors.net_quantity_value && (
              <p className="text-xs text-red-600 flex items-center gap-1" data-testid="net-quantity-error">
                <AlertCircle className="h-3 w-3" />{errors.net_quantity_value}
              </p>
            )}
          </div>
        )}

        {/* Licenses & Certificates */}
        <div className="space-y-2">
          <Label className={errors.licenses_certifications ? 'text-red-600' : ''}>
            Licenses &amp; Certificates
            {requiredLicenseType ? (
              <span className="text-red-500"> *</span>
            ) : (
              <span className="text-xs text-gray-400 ml-1 font-normal">(optional)</span>
            )}
          </Label>
          {requiredLicenseType && (
            <p className="text-xs text-amber-600" data-testid="license-required-hint">
              {LICENSE_TYPE_LABELS[requiredLicenseType] || requiredLicenseType} number is required for this category.
              You can add multiple (e.g. more than one FSSAI license).
            </p>
          )}
          <div className="space-y-3" data-testid="licenses-certifications-list">
            {licenses.map((lic, idx) => {
              const isLocked = requiredLicenseType && lic.type === requiredLicenseType && idx === licenses.findIndex((l) => l.type === requiredLicenseType);
              const isOther = lic.type === 'Other';
              return (
                <div key={idx} className="rounded-lg border p-3 space-y-2" data-testid={`license-row-wrap-${idx}`}>
                  {lic.source === 'vendor_default' && (
                    <div className="text-[11px] text-indigo-600 font-medium flex items-center gap-1" data-testid={`license-inherited-badge-${idx}`}>
                      Inherited from your Store Profile — remove here to opt this listing out
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap items-center">
                    <Select
                      value={lic.type}
                      disabled={isLocked}
                      onValueChange={(v) => updateLicense(idx, applySavedDefault({ ...lic, type: v, image_url: v === 'Other' ? lic.image_url : '' }))}
                    >
                      <SelectTrigger className="w-44 flex-shrink-0" data-testid={`license-type-select-${idx}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {LICENSE_TYPE_OPTIONS.map((t) => (
                          <SelectItem key={t} value={t}>{LICENSE_TYPE_LABELS[t] || t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={lic.number || ''}
                      onChange={(e) => updateLicense(idx, { number: e.target.value })}
                      placeholder={`${LICENSE_TYPE_LABELS[lic.type] || lic.type} number`}
                      className={`flex-1 min-w-[160px] ${errors.licenses_certifications && isLocked ? 'border-red-500' : ''}`}
                      data-testid={`license-number-input-${idx}`}
                    />
                    {lic.type === 'FSSAI' && (
                      <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto order-3 sm:order-none">
                        <VerificationBadge lic={lic} />
                        {lic.source !== 'vendor_default' && listingId && (
                          (!isAdmin && !lic.is_verified && (lic.verification_attempts || 0) >= MAX_ATTEMPTS) ? (
                            <span className="text-xs font-medium text-red-600" data-testid={`license-verify-capped-${idx}`}>
                              {MAX_ATTEMPTS}/{MAX_ATTEMPTS} attempts used — contact admin to verify
                            </span>
                          ) : (
                            <Button
                              type="button"
                              variant="default"
                              size="sm"
                              className="h-8 text-xs font-semibold shadow-sm"
                              disabled={verifyingIdx === idx || !lic.number}
                              onClick={() => (isNearExpiry(lic) ? setRenewalModalIdx(idx) : handleVerify(idx, false))}
                              data-testid={`license-verify-btn-${idx}`}
                            >
                              {verifyingIdx === idx ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
                              {lic.is_verified ? 'Re-verify' : 'Verify'}
                            </Button>
                          )
                        )}
                        {lic.source === 'vendor_default' && (
                          <span className="text-[11px] text-gray-400">Verify from your Store Profile's Store-wide Licenses card</span>
                        )}
                      </div>
                    )}
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={() => removeLicenseRow(idx)}
                        className="px-2 text-gray-400 hover:text-red-500"
                        data-testid={`license-remove-btn-${idx}`}
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                  {isOther && (
                    <div className="flex items-center gap-2">
                      {lic.image_url ? (
                        <div className="flex items-center gap-2" data-testid={`license-cert-preview-${idx}`}>
                          <img src={lic.image_url} alt="Certificate" className="w-10 h-10 rounded-lg object-cover border" />
                          <button type="button" onClick={() => updateLicense(idx, { image_url: '' })} className="text-xs text-red-500" data-testid={`license-cert-remove-btn-${idx}`}>
                            Remove
                          </button>
                        </div>
                      ) : (
                        <label className="text-xs text-indigo-600 font-medium flex items-center gap-1 cursor-pointer" data-testid={`license-cert-upload-btn-${idx}`}>
                          <Upload className="h-3.5 w-3.5" />
                          {uploadingIdx === idx ? 'Uploading...' : 'Upload Certificate Photo (optional)'}
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            disabled={uploadingIdx === idx}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) uploadCertImage(idx, file);
                              e.target.value = '';
                            }}
                          />
                        </label>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          {errors.licenses_certifications && (
            <p className="text-xs text-red-600 flex items-center gap-1" data-testid="licenses-certifications-error">
              <AlertCircle className="h-3 w-3" />{errors.licenses_certifications}
            </p>
          )}
          <button type="button" onClick={addLicenseRow} className="text-sm text-indigo-600 font-medium flex items-center gap-1" data-testid="add-license-btn">
            <Plus className="h-4 w-4" /> Add License / Certificate
          </button>
        </div>

        {/* Shelf-Life / Expiry (perishables) */}
        {complianceConfig.enable_expiry_rules && (
          <div className="grid grid-cols-2 gap-3 border-t pt-4">
            <div className="space-y-1.5">
              <Label className={errors.shelf_life_days || errors.expiry_date ? 'text-red-600' : ''}>
                {complianceConfig.expiry_legal_label || 'Best Before'} <span className="text-red-500">*</span>
              </Label>
              {complianceConfig.expiry_date_format === 'exact_date' ? (
                <Input
                  type="date"
                  value={formData.expiry_date || ''}
                  onChange={(e) => handleChange('expiry_date', e.target.value)}
                  className={errors.expiry_date ? 'border-red-500' : ''}
                  data-testid="field-expiry_date"
                />
              ) : (
                <Input
                  type="number"
                  min="0"
                  value={formData.shelf_life_days || ''}
                  onChange={(e) => handleChange('shelf_life_days', e.target.value)}
                  placeholder="Shelf life in days"
                  className={errors.shelf_life_days ? 'border-red-500' : ''}
                  data-testid="field-shelf_life_days"
                />
              )}
              {(errors.shelf_life_days || errors.expiry_date) && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{errors.shelf_life_days || errors.expiry_date}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>Freshness Guarantee on Delivery</Label>
              <Input
                type="number"
                min={shelfLifeFloorDays || 0}
                value={formData.freshness_guarantee_days || ''}
                onChange={(e) => handleChange('freshness_guarantee_days', e.target.value)}
                placeholder={shelfLifeFloorDays ? `Min ${shelfLifeFloorDays}+ days` : 'e.g. 90 days'}
                className={errors.freshness_guarantee_days ? 'border-red-500' : ''}
                data-testid="field-freshness_guarantee_days"
              />
              {shelfLifeGuaranteeWording ? (
                <p className="flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-1.5" data-testid="freshness-guarantee-wording">
                  <Shield className="h-3.5 w-3.5 shrink-0" />
                  Minimum Guaranteed Shelf Life at delivery: {shelfLifeGuaranteeWording}
                </p>
              ) : (
                <p className="text-xs text-gray-400">Optional — days of freshness you guarantee remain when delivered.</p>
              )}
              {errors.freshness_guarantee_days && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />{errors.freshness_guarantee_days}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Warranty / Guarantee */}
        {complianceConfig.enable_warranty_rules && (
          <div className="space-y-2 border-t pt-4">
            <Label className={errors.warranty_duration_value ? 'text-red-600' : ''}>
              Warranty / Guarantee <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Select
                value={formData.warranty_assurance_type || complianceConfig.default_assurance_type}
                onValueChange={(v) => handleChange('warranty_assurance_type', v)}
              >
                <SelectTrigger className="flex-1" data-testid="field-warranty_assurance_type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="manufacturer_warranty">Manufacturer Warranty</SelectItem>
                  <SelectItem value="seller_warranty">Seller Warranty</SelectItem>
                  <SelectItem value="brand_guarantee">Brand Guarantee</SelectItem>
                  <SelectItem value="none">None</SelectItem>
                </SelectContent>
              </Select>
              <Input
                type="number"
                min="0"
                value={formData.warranty_duration_value || ''}
                onChange={(e) => handleChange('warranty_duration_value', e.target.value)}
                placeholder="Duration"
                className={`w-24 ${errors.warranty_duration_value ? 'border-red-500' : ''}`}
                data-testid="field-warranty_duration_value"
              />
              <Select
                value={formData.warranty_duration_unit || complianceConfig.default_warranty_duration_unit}
                onValueChange={(v) => handleChange('warranty_duration_unit', v)}
              >
                <SelectTrigger className="w-28" data-testid="field-warranty_duration_unit">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="months">Months</SelectItem>
                  <SelectItem value="years">Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {errors.warranty_duration_value && (
              <p className="text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />{errors.warranty_duration_value}
              </p>
            )}
          </div>
        )}
      </CardContent>
      <Dialog open={renewalModalIdx !== null} onOpenChange={(open) => { if (!open) closeRenewalModal(); }}>
        <DialogContent data-testid="license-renewal-modal">
          <DialogHeader>
            <DialogTitle>Confirm License Renewal</DialogTitle>
            <DialogDescription>
              This FSSAI license is nearing or past its expiry date. Re-verifying calls Verifico
              again to fetch the latest status and expiry — please confirm you've renewed it (or
              want to re-check) before proceeding.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 py-2">
            <Checkbox
              id="license-renewal-checkbox"
              checked={renewalChecked}
              onCheckedChange={(v) => setRenewalChecked(!!v)}
              data-testid="license-renewal-checkbox"
            />
            <label htmlFor="license-renewal-checkbox" className="text-sm cursor-pointer">
              I have renewed this license
            </label>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closeRenewalModal} data-testid="license-renewal-cancel-btn">
              Cancel
            </Button>
            <Button
              type="button"
              disabled={!renewalChecked}
              onClick={() => { const idx = renewalModalIdx; closeRenewalModal(); handleVerify(idx, true); }}
              data-testid="license-renewal-confirm-btn"
            >
              Re-verify Now
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ComplianceCard;
